import { isNullOrUndefined } from '@deltasierra/type-utilities';
import { IRectangle } from '../../geometry';
import { ICanvasFont } from '../canvasFont';
import { wrapInContextTransaction } from '../canvasUtils';
import { IRenderContext } from '../renderContext';
import { BuilderFontCustomDto, BuilderFontCustomMetrics } from '../../builderFont';
import {
    convertUnits,
    POINTS_PER_INCH,
    PrintUnits,
    getFallbackFontMetrics,
    getFontMetricsForOpenSans,
} from '../../builderDocument';

export const defaultLineHeight = 1.2;

export const letterSpacingCharacter = '\u200A';

export enum HorizontalTextAlignment {
    Left = 'Left' as any,
    Center = 'Center' as any,
    Right = 'Right' as any,
    Justify = 'Justify' as any,
}

export enum VerticalTextAlignment {
    Top = 'Top' as any,
    Center = 'Center' as any,
    Bottom = 'Bottom' as any,
}

export interface IRenderResult {
    bounds: { x: number; y: number; width: number; height: number } | null;
    fontSize: number;
    lineCount: number;
}

export interface IToken {
    text: string;
}

export type IParseFunction<T> = (text: string) => ITokenIterator<T>;

export type ITokenIterator<T> = () => T | null;

export interface IRenderTextShrinkOptions {
    minFontSize?: number;
    shrinkToFitWidth?: boolean;
    shrinkToFitHeight?: boolean;
}

export interface ITextRenderOptions {
    fallbackFontFamily?: string;

    font: ICanvasFont;
    fontMetrics: BuilderFontCustomMetrics;

    bounds: IRectangle;

    wordWrap?: boolean;
    allowWordBreaks?: boolean;

    tabText?: string;

    measureOnly?: boolean;

    horizontalAlignment?: HorizontalTextAlignment;
    verticalAlignment?: VerticalTextAlignment;

    useOldTextRenderingLineHeightFix: boolean;
    useTextRenderingLineHeightFix: boolean;
}

export interface IRendererState {
    actualBounds: IRectangle | null;
    lineCount: number;
}

export type IRenderTokenFunction<
    TRenderTextOptions extends ITextRenderOptions,
    TRendererState extends IRendererState,
    TToken,
> = (context: IRenderContext, options: TRenderTextOptions, state: TRendererState, token: TToken | null) => void;

export class UnexpectedTokenType extends Error {
    public constructor(token: IToken & { type: any }) {
        super(`Unexpected Token Type: ${token.type} ('${token.text}')`);
    }
}

function wrapRenderToReturnRenderResult<TRenderTextOptions extends ITextRenderOptions>(
    render: (options: TRenderTextOptions) => IRendererState,
    options: TRenderTextOptions,
): () => IRenderResult {
    return (): IRenderResult => {
        const state = render(options);

        return {
            bounds: state.actualBounds,
            fontSize: options.font.size,
            lineCount: state.lineCount,
        };
    };
}

interface IShrinkToFitFontSizeCacheKey extends ITextRenderOptions, IRenderTextShrinkOptions {}

class ShrinkToFitFontSizeCache {
    private static defaultSize = 100;

    private cache: { [key: string]: { time: number; fontSize: number } } = {};

    private count = 0;

    private keys: string[] = [];

    public constructor(
        private maxSize: number = ShrinkToFitFontSizeCache.defaultSize,
        private preservationDuration = 3000,
    ) {}

    public getFontSize(options: IShrinkToFitFontSizeCacheKey, text: string): number | undefined {
        const key = this.getKey(options, text);
        const result = this.cache[key];


        if (result) {
            if (result.time + this.preservationDuration < Date.now()) {
                delete this.cache[key];
            } else {
                return result.fontSize;
            }
        }

        return undefined;
    }

    public setFontSize(options: IShrinkToFitFontSizeCacheKey, text: string, fontSize: number) {
        const key = this.getKey(options, text);

        if (this.count === this.maxSize) {
            const keyToRemove = this.keys.shift();
            if (keyToRemove) {
                delete this.cache[keyToRemove];
            }
        }


        if (!this.cache[key]) {
            this.keys.push(key);
            this.count++;
        }

        const time = Date.now();

        this.cache[key] = { fontSize, time };
    }

    private getKey(options: IShrinkToFitFontSizeCacheKey, text: string): string {
        return JSON.stringify(options) + text;
    }
}

const fontSizeCache = new ShrinkToFitFontSizeCache();

function wrapRenderInShrinkToFitLoop<TRenderTextOptions extends ITextRenderOptions>(
    render: (options: TRenderTextOptions) => IRendererState,
    options: IRenderTextShrinkOptions & TRenderTextOptions,
    text: string,
): () => IRenderResult {
    // eslint-disable-next-line max-statements
    return (): IRenderResult => {
        let state: IRendererState;

        const shouldRenderWhenDone = !options.measureOnly;

        let fontSizeToUse = fontSizeCache.getFontSize(options, text);

        if (!isNullOrUndefined(fontSizeToUse)) {
            options.font.size = fontSizeToUse;
            // Need to do this to avoid compilation error due to "state" being used before assignment.
            state = render(options);
        } else {
            const originalOptions = options;

            const searchSpace = {
                max: options.font.size,
                min: options.minFontSize || 1,
            };

            do {
                options.measureOnly = true;

                const searchSpaceSize = searchSpace.max - searchSpace.min;

                // Choose a font size half way in our search space
                options.font.size = searchSpace.min + Math.ceil(searchSpaceSize / 2);

                // Do measurements
                state = render(options);

                const didExceedMaxWidth =
                    state.actualBounds && state.actualBounds.width > options.bounds.width && options.shrinkToFitWidth;
                const didExceedMaxHeight =
                    state.actualBounds &&
                    state.actualBounds.height > options.bounds.height &&
                    options.shrinkToFitHeight;

                // If the font size is too big then set the max font size, else set the min font size
                if (didExceedMaxWidth || didExceedMaxHeight) {
                    searchSpace.max = options.font.size - 1;
                } else {
                    searchSpace.min = options.font.size;
                }
            } while (searchSpace.max - searchSpace.min > 0);

            fontSizeToUse = searchSpace.min;
            fontSizeCache.setFontSize(originalOptions, text, fontSizeToUse);
        }

        if (shouldRenderWhenDone) {
            options.font.size = fontSizeToUse;
            options.measureOnly = false;

            // Render for reals
            state = render(options);
        }

        return {
            bounds: state.actualBounds,
            fontSize: fontSizeToUse,
            lineCount: state.lineCount,
        };
    };
}

function getOptionsWithDefaults<TRenderTextOptions extends ITextRenderOptions>(
    options: IRenderTextShrinkOptions & TRenderTextOptions,
) {
    // Recreate options with defaults for any missing props
    options.wordWrap = isNullOrUndefined(options.wordWrap) ? false : options.wordWrap;
    options.allowWordBreaks = isNullOrUndefined(options.allowWordBreaks) ? false : options.allowWordBreaks;
    options.tabText = isNullOrUndefined(options.tabText) ? '  ' : options.tabText;
    options.shrinkToFitWidth = isNullOrUndefined(options.shrinkToFitWidth) ? false : options.shrinkToFitWidth;
    options.shrinkToFitHeight = isNullOrUndefined(options.shrinkToFitHeight) ? false : options.shrinkToFitHeight;
    options.minFontSize ||= 5;
    options.measureOnly = isNullOrUndefined(options.measureOnly) ? false : options.measureOnly;
    options.verticalAlignment = isNullOrUndefined(options.verticalAlignment)
        ? VerticalTextAlignment.Top
        : options.verticalAlignment;
    options.horizontalAlignment = isNullOrUndefined(options.horizontalAlignment)
        ? HorizontalTextAlignment.Left
        : options.horizontalAlignment;
    return options;
}

// eslint-disable-next-line max-params
export function renderText<
    TToken,
    TRenderTextOptions extends ITextRenderOptions,
    TRendererState extends IRendererState,
    TRenderTokenFunction extends IRenderTokenFunction<TRenderTextOptions, TRendererState, TToken>,
>(
    context: IRenderContext,
    text: string,
    options: IRenderTextShrinkOptions & TRenderTextOptions,
    parse: IParseFunction<TToken>,
    renderToken: TRenderTokenFunction,
    stateFactory: (context: IRenderContext, state: IRendererState, options: ITextRenderOptions) => TRendererState,
): IRenderResult {
    // Recreate options with defaults for any missing props
    const optionsWithDefaults = getOptionsWithDefaults(options);

    function renderWithOptions(opts: TRenderTextOptions): IRendererState {
        let renderTokens = () => {
            const state: TRendererState = stateFactory(
                context,
                {
                    actualBounds: null,
                    lineCount: 0,
                },
                optionsWithDefaults,
            );

            // Parse the text and get a method that retrieves the next token
            const getNextToken = parse(text);

            /*
             * Context.textBaseline = 'top';
             * Context.textAlign = 'left';
             */

            context.fontFamily(opts.font.family).fontSize(opts.font.size, opts.font.sizeUnit);

            let token: TToken | null;
            do {
                token = getNextToken();

                /*
                 * There is potential for token to be null here
                 * Which means we have reached the end of the text
                 * BUT we should still call renderToken one last time
                 * As this will be it's chance to flush any cached
                 * Tokens that still need rendering and to do any other
                 * Finalisation stuff...
                 * Could refactor this so the indented behaviour is a little
                 * More explicit...maybe also specify a finaliseRender func? Not sure..
                 */
                renderToken(context, opts, state, token);
            } while (!isNullOrUndefined(token));

            return state;
        };

        renderTokens = wrapInContextTransaction(context, renderTokens);

        return renderTokens();
    }

    // Handle shrinking text if we need to
    const render =
        optionsWithDefaults.shrinkToFitWidth || optionsWithDefaults.shrinkToFitHeight
            ? wrapRenderInShrinkToFitLoop(renderWithOptions, optionsWithDefaults, text)
            : wrapRenderToReturnRenderResult(renderWithOptions, optionsWithDefaults);

    return render();
}

export function calculateLineHeightInPixels(font: ICanvasFont, metrics: BuilderFontCustomMetrics): number {
    const height = Math.abs(metrics.bbox.maxY) + Math.abs(metrics.bbox.minY);
    const fontSizeInPoints = convertUnits(font.size, font.sizeUnit as PrintUnits, PrintUnits.points);
    const resolution = 72;
    const lineHeightInPoints = (height * fontSizeInPoints * resolution) / (POINTS_PER_INCH * metrics.unitsPerEm);
    return convertUnits(lineHeightInPoints, PrintUnits.points, PrintUnits.pixels);
}

export function findMatchingFontMetricsOrDefault(
    fontFamily: string,
    fonts: BuilderFontCustomDto[],
    fallbackFont?: string,
    fallback = getFontMetricsForOpenSans(),
): BuilderFontCustomMetrics {
    if (fallbackFont) {
        const fallbackFontMetric = getFallbackFontMetrics(fallbackFont);
        if (fallbackFontMetric) {
            return fallbackFontMetric;
        }
    }

    for (const font of fonts) {
        if (font.family === fontFamily) {
            return font.metrics;
        }
    }
    return fallback;
}

export function stripLetterSpacingCharacters(text: string): string {
    const regex = new RegExp(`${letterSpacingCharacter}+`, 'giu');
    return text.replace(regex, '');
}
